import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { Loader2, ArrowRight, ChevronDown } from "lucide-react";
import { ConcernPicker, toggleConcern, SourceSelector } from "@/lib/scriptureUi";

// The core "What's on your mind?" finder — warm, progressive, minimal typing.
export function WisdomFinder() {
  const [topics, setTopics] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [selected, setSelected] = useState([]);
  const [need, setNeed] = useState("");
  const [source, setSource] = useState("proverbs");
  const [situation, setSituation] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [showNeed, setShowNeed] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/topics").then((r) => setTopics(r.data)).catch(() => {});
    api.get("/needs").then((r) => setNeeds(r.data)).catch(() => {});
  }, []);

  const toggle = (slug, max) => setSelected((s) => toggleConcern(s, slug, max));

  const submit = async () => {
    if (selected.length === 0 && situation.trim().length < 3) {
      toast.error("Choose what's on your mind, or tell us in a few words."); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/wisdom-path", {
        issues: selected, situation, needs: need ? [need] : [], source,
      });
      track("search", { query: situation, topic: (data?.labels?.[0] || selected[0]) });
      nav("/wisdom-path", { state: { result: data, query: { issues: selected, situation, need, source } } });
    } catch {
      toast.error("Something went wrong finding your wisdom.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="wisdom-finder" className="rounded-3xl border border-[#E2E8F0] bg-white p-5 sm:p-7 shadow-sm">
      <label className="block text-lg font-serif-display text-[#0F172A]">Tell us what's on your mind.</label>
      <p className="text-sm text-[#94A3B8] mt-1 mb-4">Pick up to 3 — or just add a few words below.</p>

      <ConcernPicker topics={topics} selected={selected} onToggle={toggle} testidPrefix="concern" />

      <div className="mt-6">
        <button data-testid="toggle-details" onClick={() => setShowDetails((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm text-[#2C4C3B] font-medium">
          <ChevronDown className={`w-4 h-4 transition-transform ${showDetails ? "rotate-180" : ""}`} /> Add details (optional)
        </button>
        {showDetails && (
          <textarea data-testid="situation-input" value={situation} onChange={(e) => setSituation(e.target.value)} rows={3}
            placeholder="e.g. My daughter and I haven't spoken in months and I don't know how to reach her."
            className="mt-3 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-[#334155] leading-relaxed focus:outline-none focus:border-[#2C4C3B] resize-none" />
        )}
      </div>

      <div className="mt-5">
        <button data-testid="toggle-need" onClick={() => setShowNeed((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm text-[#2C4C3B] font-medium">
          <ChevronDown className={`w-4 h-4 transition-transform ${showNeed ? "rotate-180" : ""}`} /> What do you need right now? (optional)
        </button>
        {showNeed && (
          <div className="flex flex-wrap gap-2 mt-3">
            {needs.map((n) => (
              <button key={n} data-testid={`need-${n.toLowerCase()}`} onClick={() => setNeed(need === n ? "" : n)}
                className={`px-3.5 py-2 rounded-full text-sm transition-colors ${need === n ? "bg-[#B8912F] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#B8912F]"}`}>
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6">
        <p className="text-sm font-semibold text-[#2C4C3B] mb-2">Where should we look?</p>
        <SourceSelector value={source} onChange={setSource} testidPrefix="source" />
      </div>

      <button data-testid="find-wisdom-btn" onClick={submit} disabled={loading}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2C4C3B] text-white text-base font-semibold hover:bg-[#1E3629] transition-colors disabled:opacity-60">
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Finding wisdom…</> : <>Find Biblical Wisdom <ArrowRight className="w-5 h-5" /></>}
      </button>
    </div>
  );
}
